import { Component, Input, inject } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { FuelKpiRequest, GroupedBarChartComponent, FuelInsightsRequest } from "../../../../_shared";
import { FuelInsightsResponse } from '../../../../_shared/models/fuel-insights-response.model'
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { TotalDecreaseService } from '../../../services/total-decrease.service';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { FilterService } from '../../../../../_shared/services';
import { differenceInDays } from 'date-fns';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { GraphTrendIconComponent } from "../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component";
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { EmptyStateComponent } from "../../../../../_shared/components/states/empty-state/empty-state.component";
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-total-decrease-widget-graph',
  standalone: true,
  imports: [CommonModule, GroupedBarChartComponent, GraphTrendIconComponent, EmptyStateComponent, TranslocoDirective],
  templateUrl: './total-decrease-widget-graph.component.html',
  styleUrls: ['./total-decrease-widget-graph.component.scss']
})
export class TotalDecreaseWidgetGraphComponent extends BaseWidgetComponent {
  @Input() widgetExpandable: boolean = true;

  private transloco = inject(TranslocoService);

  protected totalDecreaseData: any[] = [];
  protected viewMode: 'all' | 'unusual' | 'driving' = 'all';
  private readonly defaultYTicks = [60, 50, 40, 30, 20, 10, 0];
  protected yTicks: number[] = this.defaultYTicks;
  hasError: boolean = false;
  errorMessage: string = '';
  override isLoading: boolean = true;
  dropdownOptions = [
    { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
  ];

  selectedUnit: MeasurementTypes = MeasurementTypes.Volume;

  public hasData(): boolean {
    if (!this.totalDecreaseData || this.totalDecreaseData.length === 0) return false;

    return this.totalDecreaseData.some(item => {
      const drivingValue = Number(item['Driving']) || 0;
      const unusualValue = Number(item['Unusual']) || 0;
      return drivingValue > 0 || unusualValue > 0;
    });
  }

  constructor(
    private totalDecreaseService: TotalDecreaseService,
    filterService: FilterService,
    private errorHandlerService: ErrorHandlerService
  ) {
    super(filterService);
  }

  fullChartClick(): void {
    // TODO: Implement modal for full chart view
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.loadTotalDecreaseGraphData();
  }

  private async getTotalDecreaseSummary(): Promise<void> {
    try {
      const request: FuelKpiRequest = {
        fuelMeasurementType: MeasurementTypes.Volume,
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!

      };

      this.cancelSubject.next();
      const summary = await this.totalDecreaseService.getTotalDecreaseSummary(request, this.cancelSubject);

      if (summary?.data) {
        this.percentageDifference = summary.data.percentageChange;
        this.value = summary.data.totalValue;
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  private async loadTotalDecreaseGraphData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: FuelInsightsRequest = {
        fuelMeasurementType: MeasurementTypes.Volume,
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval
      };

      const totalDecreasePromise: Promise<FuelInsightsResponse[]>[] = [];

      await this.getTotalDecreaseSummary();
      this.cancelSubject.next();
      totalDecreasePromise.push(this.totalDecreaseService.getDrivingConsumptionDataInsights(request, this.cancelSubject).then(r => r.data ?? []));
      totalDecreasePromise.push(this.totalDecreaseService.getFuelDecreaseInsights(request, this.cancelSubject).then(r => r.data ?? []));

      const [drivingData, decreaseData] = await Promise.all(totalDecreasePromise);

      this.totalDecreaseData = this.processResponses(drivingData, decreaseData);
      this.yTicks = this.calculateYTicks(this.totalDecreaseData);
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  private processResponses(drivingData: FuelInsightsResponse[], decreaseData: FuelInsightsResponse[]): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { data: drivingData, label: 'Driving' },
      { data: decreaseData, label: 'Unusual' }
    ];

    switch (groupingType) {
      case 'hourly': return TimeSeriesGroupingHelper.groupByHours(datasets);
      case 'daily': return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
      default: return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    // Group by day difference - covers all cases
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  public formatValue(value: number): string {
    const { unit } = this.getSelectedOption();
    const formattedValue = this.selectedUnit === MeasurementTypes.Volume
      ? Math.round(value).toString()
      : value.toFixed(2);
    return `${formattedValue} ${unit}`;
  }

  isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit.toString()));
  }

  public getSelectedUnitLabel(): string {
    return this.getSelectedOption().label;
  }

  public getTooltipUnit(): string {
    return this.getSelectedOption().unit;
  }

  private getSelectedOption() {
    return this.dropdownOptions.find(opt => opt.value === this.selectedUnit.toString()) || this.dropdownOptions[0];
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['Driving']) || 0,
      Number(item['Unusual']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks);
  }

  getArrowIcon(): string {
    const isPositive = this.isPercentChangePositive();
    return isPositive
      ? 'assets/images-fuel/yellowArrowIcon.svg'
      : 'assets/images-fuel/greenArrowIcon.svg';
  }

  getPercentChangeClass(): string {
    if (!this.hasValidPercentChange()) return '';
    return this.isPercentChangePositive() ? 'percent-increase' : 'percent-decrease';
  }

  private hasValidPercentChange(): boolean {
    return this.percentageDifference !== null && this.percentageDifference !== undefined;
  }

  private isPercentChangePositive(): boolean {
    return this.hasValidPercentChange() && this.percentageDifference! >= 0;
  }

  formatPercentChange(): string {
    if (!this.hasValidPercentChange()) return this.transloco.translate('fuelShared.charts.notAvailable');
    const sign = this.percentageDifference! > 0 ? '+' : '';
    return `${sign}${this.percentageDifference!.toFixed(2)}%`;
  }
}